"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import {
  ShoppingCart,
  Menu,
  X,
  UserRound,
  Heart,
  Sparkles,
} from "lucide-react";
import { Button, Badge } from "@eg/ui";
import { useCartStore } from "../store/cart";
import { useLikesStore } from "../store/likes";
import { useAuthStore } from "../store/auth";
import { SearchAutocomplete } from "./discovery/SearchAutocomplete";
import { useCartUI } from "./cart/CartUIProvider";
import { useRouter } from "next/navigation";
import { useTranslations } from "@/lib/i18n/I18nProvider";
import { LanguageSwitcher } from "./i18n/LanguageSwitcher";

export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [storeOpen, setStoreOpen] = useState<boolean | null>(null);
  const cartItemCount = useCartStore((state) => state.totalItems());
  const likesCount = useLikesStore((state) => state.likedIds.size);
  const user = useAuthStore((s) => s.user);
  const router = useRouter();
  const { isCartOpen, toggleCart } = useCartUI();
  const t = useTranslations();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    const isStoreOpenNow = (now = new Date()) => {
      const berlinNow = new Date(
        now.toLocaleString("en-US", { timeZone: "Europe/Berlin" }),
      );
      const day = berlinNow.getDay(); // 0 = Sunday, 6 = Saturday
      const minutes = berlinNow.getHours() * 60 + berlinNow.getMinutes();

      if (day === 0) {
        return false; // Sunday closed
      }
      if (day === 6) {
        return minutes >= 11 * 60 && minutes < 16 * 60; // Saturday 11:00-16:00
      }
      return minutes >= 11 * 60 && minutes < 18 * 60 + 30; // Mon-Fri 11:00-18:30
    };

    const updateStoreStatus = () => {
      setStoreOpen(isStoreOpenNow());
    };

    updateStoreStatus();
    const intervalId = window.setInterval(updateStoreStatus, 60_000);

    return () => window.clearInterval(intervalId);
  }, []);

  const primaryInk = "text-slate-900";
  const secondaryInk = "text-slate-700";
  const tertiaryInk = "text-slate-500";
  const actionSurface =
    "border-slate-300/80 bg-white/80 hover:border-cyan-500/45 hover:bg-white";
  const badgeSurface = "border-slate-300/80 bg-white/78";
  const storeStatusLabel =
    storeOpen === null
      ? t("misc.storeStatusPending")
      : storeOpen
        ? t("misc.storeOpen")
        : t("misc.storeClosed");

  return (
    <nav className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
      <div className="rounded-2xl border border-slate-200/80 bg-white/76 shadow-[0_18px_36px_rgba(15,23,42,0.12)] backdrop-blur-xl">
        <div className="flex flex-col gap-3 p-4 lg:flex-row lg:items-center lg:gap-6">
          <div className="flex items-center justify-between gap-4">
            <Link href="/" className="flex items-center gap-3">
              <img
                src="/Logo.png"
                alt="Echt Grandios"
                className="h-14 w-auto rounded-md shadow-sm"
              />
              <div className="hidden sm:block">
                <p
                  className={`text-xs uppercase tracking-[0.24em] ${tertiaryInk}`}
                >
                  Headshop
                </p>
                <p className={`text-sm font-semibold ${primaryInk}`}>
                  Echt Grandios Webshop
                </p>
              </div>
            </Link>
            <div className="flex items-center gap-2 lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={t("actions.openMenu")}
              >
                {mobileMenuOpen ? (
                  <X className={`${primaryInk} h-5 w-5`} />
                ) : (
                  <Menu className={`${primaryInk} h-5 w-5`} />
                )}
              </Button>
            </div>
          </div>

          <div className="hidden flex-1 lg:block">
            <SearchAutocomplete />
          </div>

          <div className="flex flex-1 items-center justify-end gap-2 lg:flex-none">
            <Badge
              variant="outline"
              className={`hidden md:inline-flex ${badgeSurface}`}
            >
              <span className={primaryInk}>18+</span>
            </Badge>
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>
            <Link
              href={user ? "/account" : "/login"}
              className={`hidden items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-white md:inline-flex ${actionSurface}`}
            >
              <UserRound className={`${primaryInk} h-4 w-4`} />
              <span className={primaryInk}>
                {user ? t("account.myAccount") : t("account.login")}
              </span>
            </Link>
            <Link href="/account/likes" className="relative hidden md:block">
              <Button variant="ghost" size="icon" className="relative">
                <Heart className={`${primaryInk} h-5 w-5`} />
                {isHydrated && likesCount > 0 ? (
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">
                    {likesCount}
                  </span>
                ) : null}
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              aria-label={t("actions.openCart")}
              aria-haspopup="dialog"
              aria-expanded={isCartOpen}
              onClick={() => {
                if (!document.cookie.includes("eg_age_confirmed=true")) {
                  router.push("/warenkorb");
                  return;
                }
                toggleCart();
              }}
            >
              <ShoppingCart className={`${primaryInk} h-5 w-5`} />
              {isHydrated && cartItemCount > 0 ? (
                <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-bold text-white">
                  {cartItemCount}
                </span>
              ) : null}
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-200/75 px-4 py-2 text-sm">
          <div className="hidden items-center gap-5 font-medium lg:flex">
            <Link
              href="/produkte"
              className={`transition ${secondaryInk} hover:opacity-100`}
            >
              {t("nav.products")}
            </Link>
            <Link
              href="/ueber-uns"
              className={`transition ${secondaryInk} hover:opacity-100`}
            >
              {t("nav.about")}
            </Link>
            <Link
              href="/kontakt"
              className={`transition ${secondaryInk} hover:opacity-100`}
            >
              {t("nav.contact")}
            </Link>
            <Link
              href="/agb"
              className={`transition ${secondaryInk} hover:opacity-100`}
            >
              {t("nav.terms")}
            </Link>
            <Link
              href="/impressum"
              className={`transition ${secondaryInk} hover:opacity-100`}
            >
              {t("nav.imprint")}
            </Link>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Sparkles className="h-4 w-4 text-amber-600" />
            <span className={secondaryInk}>
              {storeStatusLabel} · {t("misc.storefrontExperience")}
            </span>
          </div>
        </div>

        {mobileMenuOpen ? (
          <div className="border-t border-slate-200/75 px-4 py-4 lg:hidden">
            <div className="mb-3 flex justify-end">
              <LanguageSwitcher />
            </div>
            <div className="mb-3">
              <SearchAutocomplete />
            </div>
            <div className="space-y-2">
              {[
                { href: "/produkte", label: t("nav.products") },
                { href: "/ueber-uns", label: t("nav.about") },
                { href: "/kontakt", label: t("nav.contact") },
                { href: "/agb", label: t("nav.terms") },
                { href: "/impressum", label: t("nav.imprint") },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block rounded-xl border border-slate-300/70 bg-white/66 px-3 py-2 text-sm font-medium transition hover:border-cyan-500/40 hover:bg-cyan-50/65"
                >
                  <span className={primaryInk}>{link.label}</span>
                </Link>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </nav>
  );
}
